import type { AxiosRequestConfig } from 'axios';
import type {
  ApiResponse,
  IAttachment,
  IGetAttachmentRequest,
  IGetDatabaseItemRequest,
  IGetDatabaseViewExportRequest,
  IGetDatabaseViewRequest,
  IGetDatabaseViewsListRequest,
  IGetLatestDatabaseItemsRequest,
  IGetResourceRequest,
  IGetTreeRequest,
  ILatestDatabaseItem,
  IRawResourceItemType,
  IRawResourceTreeAnnouncementNodes,
  IRawResourceTreeDatabaseNodes,
  IRawResourceTreeDocumentNodes,
  IRawResourceTreeLinkNodes,
  IResourceClientMember,
  IResourceCreateAnnouncement,
  IResourceCreateDocument,
  IResourceCreateFolder,
  IResourceCreateLink,
  IResourceDatabaseField,
  IResourceDatabaseFieldType,
  IResourceDatabaseView,
  IResourceDatabaseViewItemsData,
  IResourceDeleteAnnouncement,
  IResourceDeleteDatabaseItem,
  IResourceDeleteFolder,
  IResourceDeleteLink,
  IResourceEditFolder,
  IResourceFavoriteRequest,
  IResourceFavoriteResponse,
  IResourceGetMemberListRequest,
  IResourceUpdateAnnouncement,
  IResourceUpdateDocument,
  IResourceUpsertDatabaseItem,
} from './types';
import type { IResourceType } from '@/components/common/resources/types';
import type { PayloadRequest, StandardRequest } from '@/store';

import { v3Get, v3Post, v3Remove } from './axios.instance.V3';
import { UserType } from '@/store/enums';
import { convertToFormData } from '@/helpers';

const url = {
  getTree: (resourceType: IResourceType, userType: 'su' | 'sp', clientId?: number) =>
    `${userType ?? ''}/resource/${resourceType}/tree${clientId ? `?clientId=${clientId}` : ''}`,
  getAttachment: (attachmentId: number) => `/ce/attachment/g/${attachmentId}`,
  getResource: (resourceType: string, resourceId: number) => `/resource/${resourceType}/${resourceId}`,
  getDatabaseItem: (dbId: number, viewId: number, itemId: number) =>
    `/sp/resource/database/${dbId}/view/${viewId}/item/${itemId}`,
  getDatabaseViewsList: (dbId: number, userType: 'su' | 'sp') => `${userType ?? ''}/resource/database/${dbId}/views`,
  getDatabaseView: (dbId: number, viewId: number, userType: 'su' | 'sp') =>
    `${userType ?? ''}/resource/database/${dbId}/view/${viewId}`,
  getDatabaseFieldsList: (dbId: number, userType: 'su' | 'sp') => `${userType}/resource/database/${dbId}/fields`,
  getDatabaseFieldTypes: (userType: 'su' | 'sp') => `${userType}/resource/database/types`,
  getFavoritesList: `/sp/resources/fav/list`,
  getFavoritesListAll: `/sp/resources/fav/list/all`,
  setFavorite: `/sp/resources/fav/set`,
  removeFavorite: `/sp/resources/fav/remove`,
  createLink: '/su/resource/link/create',
  updateLink: '/su/resource/link/update',
  deleteLink: '/su/resource/link/delete',
  createDocument: '/su/resource/document/create',
  updateDocument: '/su/resource/document/update',
  deleteDocument: '/su/resource/document/delete',
  createAnnouncement: '/su/resource/announcement/create',
  updateAnnouncement: '/su/resource/announcement/update',
  deleteAnnouncement: '/su/resource/announcement/delete',
  upsertDatabaseItem: (userType: 'su' | 'sp') => `${userType}/resource/database/item/upsert`,
  deleteDatabaseItem: (userType: 'su' | 'sp') => `${userType}/resource/database/item/delete`,
  deleteMultipleDatabaseItems: (userType: 'su' | 'sp') => `${userType}/resource/database/item/delete/multi`,
  createFolder: (resourceType: IResourceType) => `/su/resource/${resourceType}/folder/create`,
  updateFolder: (resourceType: IResourceType) => `/su/resource/${resourceType}/folder/update`,
  deleteFolder: (resourceType: IResourceType) => `/su/resource/${resourceType}/folder/delete`,
  getMemberList: (clientId: number, userType: 'su' | 'sp') =>
    `${userType}/resource/database/client/${clientId}/user/list`,
  getLatestDatabaseItems: (daysBack: number, userType: 'su' | 'sp') =>
    `${userType}/resource/database/latestitems/days/${daysBack}`,
  getDatabaseViewExport: (dbId: number, viewId: number, userType: 'su' | 'sp') =>
    `${userType}/resource/database/${dbId}/view/${viewId}/export`,
  getResourcesSummaryList: 'sp/resources',
};

const getResourcesSummaryList = (): ApiResponse => v3Get(url.getResourcesSummaryList);

const getLinkTree: StandardRequest<IRawResourceTreeLinkNodes, IGetTreeRequest> = (payload, config) =>
  v3Get(url.getTree('link', payload.userType <= UserType.agent ? 'sp' : 'su', payload.clientId), config);

const getAnnouncementTree: StandardRequest<IRawResourceTreeAnnouncementNodes, IGetTreeRequest> = (payload, config) =>
  v3Get(url.getTree('announcement', payload.userType <= UserType.agent ? 'sp' : 'su', payload.clientId), config);

const getDocumentTree: StandardRequest<IRawResourceTreeDocumentNodes, IGetTreeRequest> = (payload, config) =>
  v3Get(url.getTree('document', payload.userType <= UserType.agent ? 'sp' : 'su', payload.clientId), config);

const getDatabaseTree: StandardRequest<IRawResourceTreeDatabaseNodes, IGetTreeRequest> = (payload, config) =>
  v3Get(url.getTree('database', payload.userType <= UserType.agent ? 'sp' : 'su', payload.clientId), config);

const getAttachment = (
  payload: IGetAttachmentRequest,
  config?: AxiosRequestConfig,
): ApiResponse<IAttachment | string> => v3Get(url.getAttachment(payload.attachmentId), config);

const getResource: StandardRequest<IRawResourceItemType, IGetResourceRequest> = (payload, config) =>
  v3Get(url.getResource(payload.resourceType, payload.resourceId), config);

const getDatabaseItem: StandardRequest<IResourceDatabaseViewItemsData, IGetDatabaseItemRequest> = (payload, config) =>
  v3Post(url.getDatabaseItem(payload.dbId, payload.viewId, payload.itemId), config);

const getDatabaseViewsList: StandardRequest<
  IResourceDatabaseView[],
  IGetDatabaseViewsListRequest & { userType: UserType }
> = (payload, config) =>
  v3Get(url.getDatabaseViewsList(payload.dbId, payload.userType >= UserType.client ? 'su' : 'sp'), config);

const getDatabaseView: StandardRequest<
  IResourceDatabaseView['data'],
  IGetDatabaseViewRequest & { userType: UserType }
> = (payload, config) => {
  const { dbId, viewId, userType, ...body } = payload;
  return v3Post(url.getDatabaseView(dbId, viewId, userType >= UserType.client ? 'su' : 'sp'), body, config);
};

const getDatabaseFieldsList: StandardRequest<IResourceDatabaseField[], { dbId: number } & { userType: UserType }> = (
  payload,
  config,
) => v3Get(url.getDatabaseFieldsList(payload.dbId, payload.userType >= UserType.client ? 'su' : 'sp'), config);

const getDatabaseFieldTypes: StandardRequest<IResourceDatabaseFieldType[], { userType: UserType }> = (
  payload,
  config,
) => v3Get(url.getDatabaseFieldTypes(payload.userType >= UserType.client ? 'su' : 'sp'), config);

const getDatabaseViewExport: StandardRequest<string, IGetDatabaseViewExportRequest & { userType: UserType }> = (
  payload,
  config,
) => {
  const { dbId, viewId, userType, ...body } = payload;
  return v3Post(url.getDatabaseViewExport(dbId, viewId, userType >= UserType.client ? 'su' : 'sp'), body, config);
};

const setFavorite: StandardRequest<boolean, IResourceFavoriteRequest> = (payload, config) =>
  v3Post(url.setFavorite, payload, config);

const removeFavorite: StandardRequest<boolean, IResourceFavoriteRequest> = (payload, config) =>
  v3Post(url.removeFavorite, payload, config);

const getFavoritesList: StandardRequest<number[], IResourceFavoriteRequest> = (payload, config) =>
  v3Post(url.getFavoritesList, payload, config);

const getFavoritesListAll: StandardRequest<IResourceFavoriteResponse[]> = config =>
  v3Get(url.getFavoritesListAll, config);

/**
 *  Link CRUD
 */
const createLink: PayloadRequest<void, IResourceCreateLink> = (payload, config) => {
  return v3Post(url.createLink, payload, config);
};

const updateLink: PayloadRequest<number, IResourceCreateLink> = (payload, config) => {
  return v3Post(url.updateLink, payload, config);
};

const deleteLink: StandardRequest<void, IResourceDeleteLink> = (payload, config) => {
  return v3Post(url.deleteLink, payload, config);
};

/**
 *  Document CRUD
 */
const createDocument: StandardRequest<void, IResourceCreateDocument> = (payload, config) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => formData.append(key, value));
  return v3Post(url.createDocument, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
  });
};

const updateDocument: StandardRequest<void, IResourceUpdateDocument> = (payload, config) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => formData.append(key, value));
  return v3Post(url.updateDocument, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
  });
};

const deleteDocument: StandardRequest<void, number> = (payload, config) => {
  return v3Remove(url.deleteDocument + '/' + payload, config);
};

/**
 *  Announcement CRUD
 */

const createAnnouncement: StandardRequest<void, IResourceCreateAnnouncement> = (payload, config) => {
  const formData = convertToFormData(payload);
  return v3Post(url.createAnnouncement, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
  });
};

const updateAnnouncement: StandardRequest<void, IResourceUpdateAnnouncement> = (payload, config) => {
  const formData = convertToFormData(payload);
  return v3Post(url.updateAnnouncement, formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
  });
};

const deleteAnnouncement: StandardRequest<void, IResourceDeleteAnnouncement> = (payload, config) => {
  return v3Post(url.deleteAnnouncement, payload, config);
};

/**
 *  Database CRUD
 */

const getLatestDatabaseItems: StandardRequest<
  ILatestDatabaseItem[],
  IGetLatestDatabaseItemsRequest & { userType: UserType }
> = (payload, config) =>
  v3Get(url.getLatestDatabaseItems(payload.daysBack, payload.userType >= UserType.client ? 'su' : 'sp'), config);

const upsertDatabaseItem: StandardRequest<void, IResourceUpsertDatabaseItem & { userType: UserType }> = (
  payload,
  config,
) => {
  const { userType, ...data } = payload;
  const formData = convertToFormData(data);
  return v3Post(url.upsertDatabaseItem(userType >= UserType.client ? 'su' : 'sp'), formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
  });
};

const deleteDatabaseItem: StandardRequest<void, IResourceDeleteDatabaseItem & { userType: UserType }> = (
  payload,
  config,
) => {
  const { userType, ...data } = payload;
  const parsedData = data.itemsId.map(id => ({ clientId: data.clientId, databaseId: data.databaseId, itemId: id }));

  return v3Post(url.deleteMultipleDatabaseItems(userType >= UserType.client ? 'su' : 'sp'), parsedData, config);
};

const getMemberList: StandardRequest<IResourceClientMember[], IResourceGetMemberListRequest & { userType: UserType }> =
  (payload, config) => {
    const { userType, clientId } = payload;
    return v3Get(url.getMemberList(clientId, userType >= UserType.client ? 'su' : 'sp'), config);
  };

/**
 *  Folder CRUD
 */
const createFolder: StandardRequest<void, IResourceCreateFolder> = (payload, config) =>
  v3Post(url.createFolder(payload.type), payload, config);

const updateFolder: StandardRequest<void, IResourceEditFolder> = (payload, config) =>
  v3Post(url.updateFolder(payload.type), payload, config);

const deleteFolder: StandardRequest<void, IResourceDeleteFolder> = (payload, config) =>
  v3Post(url.deleteFolder(payload.type), payload, config);

export const apiResources = {
  getLinkTree,
  getAnnouncementTree,
  getDocumentTree,
  getDatabaseTree,
  getAttachment,
  getResource,
  getDatabaseView,
  getDatabaseViewExport,
  getDatabaseViewsList,
  getDatabaseFieldsList,
  getDatabaseFieldTypes,
  getFavoritesList,
  getFavoritesListAll,
  setFavorite,
  removeFavorite,
  createLink,
  updateLink,
  deleteLink,
  createFolder,
  updateFolder,
  deleteFolder,
  createDocument,
  updateDocument,
  deleteDocument,
  upsertDatabaseItem,
  deleteDatabaseItem,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  getMemberList,
  getLatestDatabaseItems,
  getDatabaseItem,
  getResourcesSummaryList,
};
